// compositionActionsByKind — the approve / reject / requestRevision wiring
// for each composer draft kind (ADR-0010).
//
// It holds ONE fact for each kind: which dispatcher the buttons call. The
// row table, the header eyebrow, the queue label and the review surface come
// from `REVIEWABLE_KIND_SPECS` in `@septima/contract`, the one declaration of
// a reviewable kind. The entry's old `table` field is gone: the generic
// workbench took it and discarded it with `void table`.
//
// Six of the seven composer drafts route through the typed reviewable family
// (`/api/agent/reviewables/:kind/...`). SCRIVENER's suitability-letter-draft
// keeps bespoke endpoints under comply-suitability, because its approve
// materialises a SuitabilityLetter; the spec's `family` field records that.

import {
    REVIEWABLE_KIND_SPECS,
    WORKBENCH_KIND_BY_AGENT,
    kindsWithFrame,
    type ComposerDraftAgent,
    type ReviewableKind,
    type StaffActionResult,
    type WorkbenchKind,
} from '@septima/contract';

import { api, runStaffEndpoint } from '@/lib/api';
import {
    approveReviewable,
    rejectReviewable,
    requestRevisionReviewable,
} from '@/lib/agent-reviewables';

/**
 * Every composer agent, read off the spec table. ATLAS's monthly-narrative
 * composer keeps its bespoke surface in `MonthlyNarrativePanel` / the
 * review-item bodies; the seven brief-based composers (ADR-0010) share the
 * generic workbench.
 */
export type ComposerAgent = ComposerDraftAgent;

/** Action verbs a reviewer can apply to a composer draft. */
export interface CompositionActionSpec {
    label: string;
    /** Dispatch this action against a draft row. */
    dispatch: (id: string, reason?: string) => Promise<StaffActionResult>;
    successToast: string;
}

export interface CompositionActions {
    approve: CompositionActionSpec;
    reject: CompositionActionSpec;
    requestRevision?: CompositionActionSpec;
}

/**
 * Build the three reviewable-family action specs for a composer draft
 * whose approve/reject/request-revision all route through the typed
 * `/api/agent/reviewables/:kind/:id/...` family.
 */
function reviewableCompositionActions(
    kind: ReviewableKind,
    opts: {
        approveLabel: string;
        approveToast: string;
        rejectToast: string;
        revisionLabel: string;
        revisionToast: string;
    },
): CompositionActions {
    return {
        approve: {
            label: opts.approveLabel,
            successToast: opts.approveToast,
            dispatch: (id) => approveReviewable(kind, id),
        },
        reject: {
            label: 'Reject',
            successToast: opts.rejectToast,
            dispatch: (id, reason) => rejectReviewable(kind, id, reason),
        },
        requestRevision: {
            label: opts.revisionLabel,
            successToast: opts.revisionToast,
            dispatch: (id, reason) =>
                requestRevisionReviewable(kind, id, reason ?? 'No reason given'),
        },
    };
}

/** Bespoke SCRIVENER dispatchers — materialise effects (writes SuitabilityLetter). */
const scrivenerApprove: CompositionActionSpec['dispatch'] = (id) =>
    runStaffEndpoint(
        api.complySuitability.approveSuitabilityLetterDraft({
            params: { id },
            body: {},
        }),
    );

const scrivenerReject: CompositionActionSpec['dispatch'] = (id, reason) =>
    runStaffEndpoint(
        api.complySuitability.rejectSuitabilityLetterDraft({
            params: { id },
            body: { reason },
        }),
    );

const scrivenerRevision: CompositionActionSpec['dispatch'] = (id, reason) =>
    runStaffEndpoint(
        api.complySuitability.requestRevisionSuitabilityLetterDraft({
            params: { id },
            body: { revisionReason: reason ?? 'No reason given' },
        }),
    ) as Promise<StaffActionResult>;

export const compositionActionsByKind: Record<WorkbenchKind, CompositionActions> = {
    // SCRIVENER's bespoke materialise effects live on the comply-suitability
    // contract (different from the generic reviewable family).
    'suitability-letter-draft': {
        approve: {
            label: 'Approve',
            dispatch: scrivenerApprove,
            successToast: 'Letter materialised',
        },
        reject: {
            label: 'Reject',
            dispatch: scrivenerReject,
            successToast: 'Draft rejected',
        },
        requestRevision: {
            label: 'Request revision',
            dispatch: scrivenerRevision,
            successToast: 'Sent back to SCRIVENER for revision',
        },
    },
    'onboarding-pack-draft': reviewableCompositionActions('onboarding-pack-draft', {
        approveLabel: 'Approve pack',
        approveToast: 'Onboarding pack issued',
        rejectToast: 'Pack draft rejected',
        revisionLabel: 'Request revision',
        revisionToast: 'Sent back to HERALD for revision',
    }),
    'client-communication-draft': reviewableCompositionActions('client-communication-draft', {
        approveLabel: 'Approve communication',
        approveToast: 'Communication approved',
        rejectToast: 'Communication rejected',
        revisionLabel: 'Request revision',
        revisionToast: 'Sent back to EMISSARY for revision',
    }),
    'fund-report-draft': reviewableCompositionActions('fund-report-draft', {
        approveLabel: 'Approve report',
        approveToast: 'Fund report published',
        rejectToast: 'Report draft rejected',
        revisionLabel: 'Request revision',
        revisionToast: 'Sent back to CHARTER for revision',
    }),
    'vulnerability-adaptation-plan-draft': reviewableCompositionActions(
        'vulnerability-adaptation-plan-draft',
        {
            approveLabel: 'Approve plan',
            approveToast: 'Adaptation plan attached',
            rejectToast: 'Plan draft rejected',
            revisionLabel: 'Request revision',
            revisionToast: 'Sent back to HAVEN for revision',
        },
    ),
    'governance-paper-draft': reviewableCompositionActions('governance-paper-draft', {
        approveLabel: 'Approve paper',
        approveToast: 'Governance paper finalised',
        rejectToast: 'Paper draft rejected',
        revisionLabel: 'Request revision',
        revisionToast: 'Sent back to FORUM for revision',
    }),
    'fair-value-assessment-draft': reviewableCompositionActions('fair-value-assessment-draft', {
        approveLabel: 'Approve assessment',
        approveToast: 'Fair-value assessment finalised',
        rejectToast: 'Assessment draft rejected',
        revisionLabel: 'Request revision',
        revisionToast: 'Sent back to ARBITER for revision',
    }),
};

/** The workbench kind a composer agent key emits. The review-item body still
 * names the agent, and the workbench needs the kind, so this turns one into
 * the other off the contract's own map. */
export function workbenchKindForAgent(agent: ComposerDraftAgent): WorkbenchKind {
    const kind = WORKBENCH_KIND_BY_AGENT.get(agent);
    if (!kind) throw new Error(`no composer kind declares agent ${agent}`);
    return kind;
}

/**
 * Runtime exhaustiveness assertion — see `assertFindingConfigExhaustive`.
 * The typed `Record<WorkbenchKind, …>` already fails to compile on a missing
 * entry; this catches a map hand-edited out of step with the spec table.
 */
export function assertCompositionConfigExhaustive(): WorkbenchKind[] {
    const all = kindsWithFrame('workbench') as WorkbenchKind[];
    const missing = all.filter((kind) => !(kind in compositionActionsByKind));
    if (missing.length > 0) {
        throw new Error(`compositionActionsByKind missing entries: ${missing.join(', ')}`);
    }
    return all;
}
